/**
 * Start time, minutes and place of a task (#2631). A lead or holder edits the
 * three fields in the task drawer. Each change is one PATCH, so each writes one
 * history event. Everyone else reads them as plain text. The start time shows
 * and edits in the team's time zone (#2721).
 */
import { useRef, useState } from "react";

import { useMapTheme } from "../theme/MapThemeContext";
import { useTeamZone } from "../theme/TeamZoneContext";
import {
  appDate,
  isoToZonedInput,
  timeOfDay,
  zoneDiffers,
  zonedInputToIso,
} from "./taskTimingMeta";

export interface TimingPatch {
  startsAt?: string | null;
  durationMinutes?: number | null;
  place?: string | null;
}
export type TimingPatchHandler = (patch: TimingPatch) => void;

interface TimingNode {
  startsAt?: string | null | undefined;
  durationMinutes?: number | null | undefined;
  place?: string | null | undefined;
}

/** ISO instant to the `YYYY-MM-DDTHH:mm` a datetime-local input wants, in the team zone. */
export function toLocalInput(iso: string | null | undefined, timeZone?: string): string {
  return isoToZonedInput(iso, timeZone);
}

export function startLabel(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    ...(timeZone && zoneDiffers(timeZone) ? { timeZone } : {}),
  });
}

export function TaskTimingRow({
  node,
  canEdit,
  onSet,
}: {
  node: TimingNode;
  canEdit: boolean;
  onSet: TimingPatchHandler | undefined;
}) {
  const theme = useMapTheme();
  const timeZone = useTeamZone();
  const hint = zoneDiffers(timeZone) ? " (team time)" : "";
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const startsRef = useRef<HTMLInputElement>(null);
  const [place, setPlace] = useState(node.place ?? "");
  const [minutes, setMinutes] = useState(
    node.durationMinutes == null ? "" : String(node.durationMinutes),
  );
  if (!canEdit || !onSet) {
    if (!node.startsAt && !node.durationMinutes && !node.place) return null;
    const parts = [
      `${startLabel(node.startsAt, timeZone)}${node.startsAt ? hint : ""}`,
      node.durationMinutes ? `${node.durationMinutes} min` : "",
      node.place ?? "",
    ].filter(Boolean);
    return (
      <p data-testid="drawer-timing-readonly" className="mb-2 text-sm">
        {parts.join(" · ")}
      </p>
    );
  }
  const labelStyle = editorial
    ? { color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.06em" }
    : undefined;
  const labelClass = editorial
    ? "w-[100px] shrink-0 text-[10px] font-semibold uppercase"
    : "w-20 shrink-0";
  const startText = node.startsAt
    ? `${appDate(node.startsAt, timeZone)}, ${timeOfDay(node.startsAt, timeZone)}${hint}`
    : "";
  return (
    <div data-testid="drawer-timing" className="mb-2 flex flex-col gap-1.5 text-sm">
      <div className="flex items-center gap-3">
        <span className={labelClass} style={labelStyle}>
          Starts
        </span>
        <button
          type="button"
          data-testid="timing-starts-text"
          className="rounded-sm text-left text-[12.5px] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => {
            const el = startsRef.current;
            if (!el) return;
            // Focus first: the field saves on blur, so a picked time needs the focus.
            el.focus();
            if (typeof el.showPicker === "function") el.showPicker();
          }}
        >
          {startText || "Set a start time"}
        </button>
        {/* The native input stays for the picker and keyboard entry. It is
            visually hidden so the date reads in the app's own format. */}
        <input
          ref={startsRef}
          type="datetime-local"
          aria-label="Starts"
          data-testid="timing-starts"
          className="h-px w-px opacity-0"
          tabIndex={-1}
          defaultValue={toLocalInput(node.startsAt, timeZone)}
          onBlur={(e) => {
            const v = e.target.value;
            const next = v ? zonedInputToIso(v, timeZone) : null;
            if (next !== (node.startsAt ?? null)) onSet({ startsAt: next });
          }}
        />
      </div>
      <label className="flex items-center gap-3">
        <span className={labelClass} style={labelStyle}>
          Minutes
        </span>
        <input
          type="number"
          min={1}
          max={1440}
          data-testid="timing-minutes"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          onBlur={() => {
            const next = minutes === "" ? null : Number(minutes);
            if (next !== (node.durationMinutes ?? null)) onSet({ durationMinutes: next });
          }}
        />
      </label>
      <label className="flex items-center gap-3">
        <span className={labelClass} style={labelStyle}>
          Place
        </span>
        <input
          type="text"
          maxLength={200}
          data-testid="timing-place"
          value={place}
          onChange={(e) => setPlace(e.target.value)}
          onBlur={() => {
            const next = place.trim() === "" ? null : place.trim();
            if (next !== (node.place ?? null)) onSet({ place: next });
          }}
        />
      </label>
    </div>
  );
}
